import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from "@material-ui/core/Tooltip";
import BackToTopButton from './BackToTopButton';

function ConfirmationDialog({ isOpen, onConfirm, onCancel, message }) {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>Confirm Deletion</h3>
        <p>{message}</p>
        <div className="confirmation-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Remove</button>
        </div>
      </div>
    </div>
  );
}

function Favorites({data}) {

  const [listData, setlistData] = useState(data);
  const STORAGE_KEY = 'di.search.favorites';
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      if (typeof window === 'undefined') return '';
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; ticker: string | null; item: any | null; bulk: boolean }>({
    isOpen: false,
    ticker: null,
    item: null,
    bulk: false,
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, searchTerm);
      }
    } catch {
      // ignore localStorage failures (private mode / blocked storage)
    }
  }, [searchTerm]);

  const filteredList = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return listData;

    return listData.filter((item) => {
      const searchableFields = [
        item.ticker,
        item.company,
        item.default_valuation?.recommendation,
        item.default_valuation?.decision,
        item.default_valuation?.price_in_words,
        item.quote?.change_percent,
        item.default_valuation?.margin_of_safety_in_words,
        item.default_valuation?.payback_time?.current_payback_time,
        `${item.value_score ?? ''}`,
      ];

      return searchableFields.some((field) =>
        (field ?? '').toString().toLowerCase().includes(query)
      );
    });
  }, [listData, searchTerm]);

  function updatelistAPI(ticker) {
    const url = "/favorite_item/" + ticker;
    const msg = fetch(url, {method: 'DELETE'})
      .then(resp => resp.json)

    return msg;
  }

  function updateList(e, item) {
    if (e) {
      e.preventDefault();
    }
    const list = data.filter(i => i.ticker !== item.ticker)
    updatelistAPI(item.ticker)
    return setlistData(list);
  }

  function toggleSelectItem(ticker: string) {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    const visibleTickers = filteredList.map(i => i.ticker);
    const allSelected = visibleTickers.length > 0 && visibleTickers.every(t => selectedItems.has(t));
    if (allSelected) {
      setSelectedItems(prev => {
        const next = new Set(prev);
        visibleTickers.forEach(t => next.delete(t));
        return next;
      });
    } else {
      setSelectedItems(prev => {
        const next = new Set(prev);
        visibleTickers.forEach(t => next.add(t));
        return next;
      });
    }
  }

  function deleteSelected() {
    const tickers = Array.from(selectedItems);
    Promise.all(tickers.map(t => updatelistAPI(t)));
    const remaining = listData.filter(i => !selectedItems.has(i.ticker));
    setlistData(remaining);
    setSelectedItems(new Set());
  }

  function truncate(str, n){
    return (str.length > n) ? str.substr(0, n-1) + '...' : str;
  };

  function recommendationClass(decision_color) {
    if (decision_color == 'red') {
      return 'recommendation-badge red'
    } else if (decision_color == 'green') {
      return 'recommendation-badge green'
    } else {
      return 'recommendation-badge blue'
    }
  }

  function tableRowClass(decision_color) {
    if (decision_color == 'red') {
      return 'table-row red-row'
    } else if (decision_color == 'green') {
      return 'table-row green-row'
    } else if (decision_color == 'blue') {
      return 'table-row blue-row'
    } else {
      return 'table-row gray-row'
    }
  }

  function getChangeIcon(changePercent) {
    if (!changePercent) return null;
    const isPositive = changePercent.includes('+') || parseFloat(changePercent) > 0;
    return isPositive ? '↗' : '↘';
  }

  return (
    <div className="favorites-container">
      <style>{`
        .favorites-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
        }

        .favorites-header {
          background: #ffffff;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          border-bottom: 1px solid #e5e7eb;
        }

        .header-content {
          max-width: 80rem;
          margin: 0 auto;
          padding: 1.5rem 1rem;
        }

        .header-flex {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .header-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
        }

        .breadcrumb ol {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .breadcrumb li {
          display: flex;
          align-items: center;
        }

        .breadcrumb a {
          color: #6b7280;
          text-decoration: none;
          transition: color 0.15s;
        }

        .breadcrumb a:hover {
          color: #2563eb;
        }

        .breadcrumb .separator {
          width: 1rem;
          height: 1rem;
          color: #9ca3af;
          margin: 0 0.5rem;
        }

        .breadcrumb .current {
          color: #2563eb;
          font-weight: 500;
        }

        .item-counter {
          background-color: #dbeafe;
          color: #1e40af;
          padding: 0.5rem 1rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .main-content {
          margin: 0 auto;
          padding: 1.5rem 1rem;
        }

        .favorites-card {
          background: #ffffff;
          border-radius: 1rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #f3f4f6;
          overflow: hidden;
        }

        .card-header {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .card-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        .card-subtitle {
          color: #4b5563;
          margin-top: 0.25rem;
          margin-bottom: 0;
        }

        .card-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .search-box {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 9999px;
          padding: 0.5rem 1rem 0.5rem 2.75rem;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
        }

        .search-box:focus-within {
          border-color: #2563eb;
          box-shadow: 0 10px 25px -10px rgba(37, 99, 235, 0.35);
          background: #ffffff;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          width: 1rem;
          height: 1rem;
          color: #6b7280;
        }

        .search-field {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.95rem;
          color: #111827;
        }

        .search-field::placeholder {
          color: #9ca3af;
        }

        .search-clear {
          background: none;
          border: none;
          color: #6b7280;
          font-weight: 600;
          cursor: pointer;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          transition: background-color 0.15s, color 0.15s;
        }

        .search-clear:hover {
          background-color: #f3f4f6;
          color: #111827;
        }

        .search-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #4b5563;
          white-space: nowrap;
        }

        .search-meta strong {
          color: #111827;
        }

        .table-container {
          overflow-x: auto;
        }

        .favorites-table {
          width: 100%;
          border-collapse: collapse;
        }

        .favorites-table th {
          background-color: #f9fafb;
          padding: 0.5rem 0.625rem;
          text-align: left;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .favorites-table td {
          padding: 0.4375rem 0.625rem;
          white-space: nowrap;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.8125rem;
        }

        .table-row {
          transition: background-color 0.15s;
        }

        .table-row:hover {
          background-color: #f3f4f6 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .red-row {
          border-left: 4px solid #ef4444;
          background-color: rgba(254, 242, 242, 0.3);
        }

        .red-row:hover {
          background-color: rgba(254, 242, 242, 0.8) !important;
        }

        .green-row {
          border-left: 4px solid #22c55e;
          background-color: rgba(220, 252, 231, 0.3);
        }

        .green-row:hover {
          background-color: rgba(220, 252, 231, 0.8) !important;
        }

        .blue-row {
          border-left: 4px solid #3b82f6;
          background-color: rgba(219, 234, 254, 0.3);
        }

        .blue-row:hover {
          background-color: rgba(219, 234, 254, 0.8) !important;
        }

        .gray-row {
          border-left: 4px solid #6b7280;
          background-color: rgba(249, 250, 251, 0.3);
        }

        .gray-row:hover {
          background-color: rgba(249, 250, 251, 0.8) !important;
        }

        .ticker-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          font-size: 0.8125rem;
          font-weight: 600;
          background-color: #dbeafe;
          color: #1e40af;
          text-decoration: none;
          transition: background-color 0.15s;
        }

        .ticker-badge:hover {
          background-color: #bfdbfe;
        }

        .ticker-icon {
          width: 0.625rem;
          height: 0.625rem;
          margin-left: 0.25rem;
        }

        .recommendation-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.0625rem 0.4375rem;
          border-radius: 9999px;
          font-size: 0.6875rem;
          font-weight: 500;
          border: 1px solid;
        }

        .recommendation-badge.red {
          background-color: #fee2e2;
          color: #991b1b;
          border-color: #fecaca;
        }

        .recommendation-badge.green {
          background-color: #dcfce7;
          color: #166534;
          border-color: #bbf7d0;
        }

        .recommendation-badge.blue {
          background-color: #dbeafe;
          color: #1e40af;
          border-color: #bfdbfe;
        }

        .change-indicator {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .change-icon {
          font-size: 0.9375rem;
        }

        .change-value {
          font-size: 0.8125rem;
          font-weight: 600;
        }

        .progress-container {
          display: flex;
          align-items: center;
        }

        .progress-bar {
          width: 2.5rem;
          background-color: #e5e7eb;
          border-radius: 9999px;
          height: 0.375rem;
          margin-right: 0.375rem;
        }

        .progress-fill {
          height: 0.375rem;
          border-radius: 9999px;
          background: linear-gradient(to right, #4ade80, #3b82f6);
          transition: width 0.3s;
        }

        .progress-value {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #111827;
        }

        .delete-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.75rem;
          height: 1.75rem;
          border-radius: 9999px;
          background-color: #fee2e2;
          color: #dc2626;
          border: none;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .delete-button:hover {
          background-color: #fecaca;
        }

        .delete-icon {
          width: 0.875rem;
          height: 0.875rem;
        }

        .empty-state {
          background: #ffffff;
          border-radius: 1rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #f3f4f6;
          padding: 3rem 2rem;
          text-align: center;
        }

        .empty-content {
          max-width: 28rem;
          margin: 0 auto;
        }

        .empty-icon {
          width: 6rem;
          height: 6rem;
          background-color: #f3f4f6;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .empty-icon svg {
          width: 3rem;
          height: 3rem;
          color: #9ca3af;
        }

        .empty-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: #4b5563;
          margin-bottom: 1.5rem;
        }

        .browse-button {
          display: inline-flex;
          align-items: center;
          padding: 0.75rem 1.5rem;
          border: 1px solid transparent;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 0.5rem;
          color: #ffffff;
          background-color: #2563eb;
          text-decoration: none;
          transition: background-color 0.15s;
        }

        .browse-button:hover {
          background-color: #1d4ed8;
        }

        .browse-icon {
          width: 1rem;
          height: 1rem;
          margin-left: 0.5rem;
        }

        .company-name {
          font-size: 0.8125rem;
          font-weight: 500;
          color: #111827;
        }

        .decision-text {
          font-size: 0.8125rem;
          color: #111827;
          font-weight: 500;
        }

        .price-text {
          font-size: 0.8125rem;
          font-weight: 600;
        }

        .safety-text {
          font-size: 0.8125rem;
          color: #111827;
        }

        .payback-text {
          font-size: 0.8125rem;
          color: #111827;
        }

        /* Confirmation Dialog Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background-color: white;
          padding: 2rem;
          border-radius: 0.75rem;
          max-width: 28rem;
          width: 90%;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 1rem 0;
        }

        .modal-content p {
          color: #4b5563;
          margin: 0 0 1.5rem 0;
          line-height: 1.5;
        }

        .confirmation-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .btn-secondary {
          background-color: #6b7280;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #4b5563;
        }

        .btn-danger {
          background-color: #dc2626;
          color: white;
        }

        .btn-danger:hover {
          background-color: #b91c1c;
        }

        .select-checkbox {
          width: 0.9375rem;
          height: 0.9375rem;
          accent-color: #2563eb;
          cursor: pointer;
        }

        .selected-row {
          background-color: rgba(219, 234, 254, 0.5) !important;
        }

        .selection-bar {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1.5rem;
          background: #eff6ff;
          border-bottom: 1px solid #bfdbfe;
        }

        .selection-bar .selection-count {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e40af;
        }

        .selection-bar .selection-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-left: auto;
        }

        .btn-outline {
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.8125rem;
          font-weight: 500;
          border: 1px solid #d1d5db;
          background: #ffffff;
          color: #374151;
          cursor: pointer;
          transition: background-color 0.15s, border-color 0.15s;
        }

        .btn-outline:hover {
          background-color: #f3f4f6;
          border-color: #9ca3af;
        }

        .btn-danger-sm {
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.8125rem;
          font-weight: 500;
          border: none;
          background-color: #dc2626;
          color: white;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .btn-danger-sm:hover {
          background-color: #b91c1c;
        }

        .dark-mode .favorites-container {
          background: linear-gradient(135deg, #151519 0%, #1a191d 100%);
        }

        .dark-mode .favorites-header,
        .dark-mode .card-toolbar,
        .dark-mode .favorites-card,
        .dark-mode .empty-state,
        .dark-mode .modal-content {
          background: #222328;
          color: #ffffff;
          border-color: #322f2f;
        }

        .dark-mode .card-header {
          background: linear-gradient(to right, #1a191d, #222328);
          border-bottom-color: #322f2f;
        }

        .dark-mode .header-title,
        .dark-mode .card-title,
        .dark-mode .company-name,
        .dark-mode .decision-text,
        .dark-mode .safety-text,
        .dark-mode .payback-text,
        .dark-mode .progress-value,
        .dark-mode .modal-content h3 {
          color: #ffffff;
        }

        .dark-mode .breadcrumb a,
        .dark-mode .card-subtitle,
        .dark-mode .search-meta,
        .dark-mode .empty-description,
        .dark-mode .modal-content p {
          color: #9c9fa6;
        }

        .dark-mode .breadcrumb .separator {
          color: #9c9fa6;
        }

        .dark-mode .item-counter,
        .dark-mode .ticker-badge {
          background-color: rgba(25, 138, 227, 0.18);
          color: #90caf9;
        }

        .dark-mode .search-box {
          background: #151519;
          border-color: #322f2f;
        }

        .dark-mode .search-box:focus-within {
          border-color: #b66dff;
          box-shadow: 0 10px 25px -10px rgba(182, 109, 255, 0.35);
          background: #222328;
        }

        .dark-mode .search-icon,
        .dark-mode .search-clear,
        .dark-mode .search-field::placeholder {
          color: #9c9fa6;
        }

        .dark-mode .search-field,
        .dark-mode .search-meta strong,
        .dark-mode .favorites-table th,
        .dark-mode .favorites-table td {
          color: #ffffff;
        }

        .dark-mode .search-clear:hover {
          background-color: #1a191d;
          color: #ffffff;
        }

        .dark-mode .favorites-table th {
          background-color: #1a191d;
        }

        .dark-mode .favorites-table td,
        .dark-mode .favorites-table th,
        .dark-mode .card-toolbar,
        .dark-mode .selection-bar {
          border-color: #322f2f;
        }

        .dark-mode .table-row:hover {
          background-color: rgba(255, 255, 255, 0.04) !important;
        }

        .dark-mode .red-row {
          background-color: rgba(239, 68, 68, 0.12);
        }

        .dark-mode .red-row:hover {
          background-color: rgba(239, 68, 68, 0.2) !important;
        }

        .dark-mode .green-row {
          background-color: rgba(34, 197, 94, 0.12);
        }

        .dark-mode .green-row:hover {
          background-color: rgba(34, 197, 94, 0.2) !important;
        }

        .dark-mode .blue-row {
          background-color: rgba(59, 130, 246, 0.12);
        }

        .dark-mode .blue-row:hover {
          background-color: rgba(59, 130, 246, 0.2) !important;
        }

        .dark-mode .gray-row {
          background-color: rgba(156, 163, 175, 0.08);
        }

        .dark-mode .gray-row:hover {
          background-color: rgba(156, 163, 175, 0.16) !important;
        }

        .dark-mode .progress-bar,
        .dark-mode .empty-icon,
        .dark-mode .btn-outline {
          background-color: #151519;
          border-color: #322f2f;
        }

        .dark-mode .delete-button {
          background-color: rgba(220, 38, 38, 0.16);
          color: #ff9b9b;
        }

        .dark-mode .delete-button:hover {
          background-color: rgba(220, 38, 38, 0.28);
        }

        .dark-mode .selection-bar {
          background: rgba(182, 109, 255, 0.12);
        }

        .dark-mode .selection-bar .selection-count,
        .dark-mode .btn-outline,
        .dark-mode .empty-title {
          color: #ffffff;
        }

        .dark-mode .btn-outline:hover {
          background-color: #1a191d;
          border-color: #9c9fa6;
        }

        .dark-mode .selected-row {
          background-color: rgba(182, 109, 255, 0.18) !important;
        }
      `}</style>

      {/* Header Section */}
      <div className="favorites-header">
        <div className="header-content">
          <div className="header-flex">
            <div>
              <h1 className="header-title">Favorites</h1>
              <nav className="breadcrumb" aria-label="Breadcrumb">
                <ol>
                  <li>
                    <a href="/">Home</a>
                  </li>
                  <li>
                    <svg className="separator" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <a href="/dashboard">Dashboard</a>
                  </li>
                  <li>
                    <svg className="separator" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="current">Favorites</span>
                  </li>
                </ol>
              </nav>
            </div>
            <div className="item-counter">
              {listData.length} {listData.length === 1 ? 'Item' : 'Items'}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {listData.length > 0 ? (
          <div className="favorites-card">
            {/* Table Header */}
            <div className="card-header">
              <h2 className="card-title">Favorite Stocks</h2>
              <p className="card-subtitle">Your saved investment opportunities and analysis</p>
            </div>

            <div className="card-toolbar">
              <label className="search-box" htmlFor="favorites-search">
                <svg className="search-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387a1 1 0 01-1.414 1.414L12.9 14.32zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  id="favorites-search"
                  className="search-field"
                  type="search"
                  placeholder="Search tickers, companies, decisions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                )}
              </label>
              <div className="search-meta">
                <strong>{filteredList.length}</strong>
                of
                <strong>{listData.length}</strong>
                showing
              </div>
            </div>

            {selectedItems.size > 0 && (
              <div className="selection-bar">
                <span className="selection-count">{selectedItems.size} selected</span>
                <div className="selection-actions">
                  <button className="btn-outline" onClick={() => setSelectedItems(new Set())}>
                    Deselect All
                  </button>
                  <button
                    className="btn-danger-sm"
                    onClick={() =>
                      setConfirmDelete({
                        isOpen: true,
                        ticker: null,
                        item: null,
                        bulk: true,
                      })
                    }
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            )}

            {/* Responsive Table */}
            <div className="table-container">
              <table className="favorites-table">
                <thead>
                  <tr>
                    <th style={{ width: '2.25rem', paddingLeft: '0.75rem' }}>
                      <input
                        type="checkbox"
                        className="select-checkbox"
                        checked={filteredList.length > 0 && filteredList.every(i => selectedItems.has(i.ticker))}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Ticker</th>
                    <th>Company</th>
                    <th>Consider.</th>
                    <th>Decision</th>
                    <th>Price</th>
                    <th>Change</th>
                    <th>MoS</th>
                    <th>Payback</th>
                    <th>Score</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                        No favorites match your search yet.
                      </td>
                    </tr>
                  )}
                  {filteredList.map((item, index) => (
                    <tr
                      key={index}
                      className={`${tableRowClass(item.default_valuation?.recommendation_color)}${selectedItems.has(item.ticker) ? ' selected-row' : ''}`}
                    >
                      <td style={{ paddingLeft: '0.75rem' }}>
                        <input
                          type="checkbox"
                          className="select-checkbox"
                          checked={selectedItems.has(item.ticker)}
                          onChange={() => toggleSelectItem(item.ticker)}
                        />
                      </td>
                      <td>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={item.url}
                          className="ticker-badge"
                        >
                          {item.ticker}
                          <svg className="ticker-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                          </svg>
                        </a>
                      </td>
                      <td>
                        <div className="company-name">
                          {truncate(item.company, 20)}
                        </div>
                      </td>
                      <td>
                        <span className={recommendationClass(item.default_valuation?.recommendation_color)}>
                          {item.default_valuation?.recommendation}
                        </span>
                      </td>
                      <td>
                        <div className="decision-text">
                          {item.default_valuation?.decision}
                        </div>
                      </td>
                      <td>
                        <div className="price-text" style={{ color: item.quote?.change_percent_color }}>
                          {item.default_valuation?.price_in_words}
                        </div>
                      </td>
                      <td>
                        <div className="change-indicator">
                          <span className="change-icon">{getChangeIcon(item.quote?.change_percent)}</span>
                          <span
                            className="change-value"
                            style={{ color: item.quote?.change_percent_color }}
                          >
                            {item.quote?.change_percent}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="safety-text">
                          {item.default_valuation?.margin_of_safety_in_words}
                        </div>
                      </td>
                      <td>
                        <div className="payback-text">
                          {item.default_valuation?.payback_time?.current_payback_time}
                        </div>
                      </td>
                      <td>
                        <div className="progress-container">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${item.value_score}%` }}
                            ></div>
                          </div>
                          <span className="progress-value">{item.value_score}%</span>
                        </div>
                      </td>
                      <td>
                        <Tooltip
                          title={`Remove ${item.ticker}`}
                          placement="bottom"
                          arrow
                        >
                          <button
                            onClick={(e) => {
                              setConfirmDelete({ isOpen: true, ticker: item.ticker, item: item, bulk: false });
                            }}
                            className="delete-button"
                          >
                            <svg className="delete-icon" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-content">
              <div className="empty-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="empty-title">Your favorites list is empty</h3>
              <p className="empty-description">Start building your favorites by adding stocks you love and want to track.</p>
              <a
                href="/"
                className="browse-button"
              >
                Browse Stocks
                <svg className="browse-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDelete.isOpen && (
        <ConfirmationDialog
          isOpen={confirmDelete.isOpen}
          onConfirm={() => {
            if (confirmDelete.bulk) {
              deleteSelected();
            } else {
              updateList(null, confirmDelete.item);
            }
            setConfirmDelete({ isOpen: false, ticker: null, item: null, bulk: false });
          }}
          onCancel={() => setConfirmDelete({ isOpen: false, ticker: null, item: null, bulk: false })}
          message={
            confirmDelete.bulk
              ? `Are you sure you want to remove ${selectedItems.size} item${selectedItems.size === 1 ? '' : 's'} from your favorites?`
              : `Are you sure you want to remove ${confirmDelete.ticker} from your favorites?`
          }
        />
      )}
    </div>
  );
}

export default Favorites;
